// 어드민 활동 달력 컴포넌트
import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, BookOpen, Clock } from 'lucide-react';

interface CalendarData {
  [date: string]: {
    meditationCount: number;
    prayerCount: number;
    prayerDuration: number;
  };
}

interface CalendarViewProps {
  calendarData: CalendarData;
  onMonthChange: (year: number, month: number) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ calendarData, onMonthChange }) => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    onMonthChange(currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  // 시간을 시:분 형식으로 변환
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    }
    return `${minutes}분`;
  };

  // 이전 달
  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // 다음 달
  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // 해당 월의 날짜 배열 생성
  const getDaysInMonth = () => {
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 (일요일) ~ 6 (토요일)

    const days = [];
    
    // 이전 달 날짜로 빈 칸 채우기
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // 현재 달 날짜
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const days = getDaysInMonth();
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  // 날짜 키 생성
  const getDateKey = (day: number) => {
    const month = currentMonth.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${currentYear}-${month}-${dayStr}`;
  };

  // 오늘 날짜 확인
  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() + 1 &&
      currentYear === today.getFullYear()
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-6 h-6 text-emerald-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            활동 달력
          </h2>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={prevMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="text-xl font-bold text-gray-800 min-w-[150px] text-center">
            {currentYear}년 {currentMonth}월
          </div>
          
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* 범례 */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-rose-500" />
          <span className="text-sm text-gray-600">묵상 완료</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-500" />
          <span className="text-sm text-gray-600">기도 완료</span>
        </div>
      </div>

      {/* 달력 그리드 */}
      <div className="grid grid-cols-7 gap-2">
        {/* 요일 헤더 */}
        {weekDays.map((day, index) => (
          <div
            key={day}
            className={`text-center font-bold py-2 text-sm ${
              index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
            }`}
          >
            {day}
          </div>
        ))}

        {/* 날짜 셀 */}
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="aspect-square"></div>;
          }

          const dateKey = getDateKey(day);
          const data = calendarData[dateKey];
          const today = isToday(day);

          return (
            <div
              key={day}
              className={`aspect-square border rounded-lg p-2 transition-all hover:shadow-md ${
                today
                  ? 'bg-blue-50 border-blue-500 border-2'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`text-right text-sm font-semibold mb-1 ${
                index % 7 === 0 ? 'text-red-600' : index % 7 === 6 ? 'text-blue-600' : 'text-gray-700'
              }`}>
                {day}
              </div>
              
              {data && (
                <div className="space-y-1">
                  {data.meditationCount > 0 && (
                    <div className="flex items-center gap-1 bg-rose-50 rounded px-1 py-0.5">
                      <BookOpen className="w-3 h-3 text-rose-500" />
                      <span className="text-xs font-semibold text-rose-700">
                        {data.meditationCount}
                      </span>
                    </div>
                  )}
                  
                  {data.prayerCount > 0 && (
                    <div className="flex items-center gap-1 bg-purple-50 rounded px-1 py-0.5">
                      <Clock className="w-3 h-3 text-purple-500" />
                      <span className="text-xs font-semibold text-purple-700">
                        {data.prayerCount}
                      </span>
                    </div>
                  )}
                  
                  {data.prayerDuration > 0 && (
                    <div className="text-xs text-gray-500 text-center">
                      {formatDuration(data.prayerDuration)}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 하단 통계 */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-3">이번 달 통계</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-rose-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-rose-500" />
              <span className="text-sm font-semibold text-rose-700">총 묵상 완료</span>
            </div>
            <div className="text-2xl font-bold text-rose-700">
              {Object.values(calendarData).reduce((sum, day) => sum + day.meditationCount, 0)}명
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-semibold text-purple-700">총 기도 완료</span>
            </div>
            <div className="text-2xl font-bold text-purple-700">
              {Object.values(calendarData).reduce((sum, day) => sum + day.prayerCount, 0)}명
            </div>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              <span className="text-sm font-semibold text-indigo-700">총 기도 시간</span>
            </div>
            <div className="text-2xl font-bold text-indigo-700">
              {formatDuration(Object.values(calendarData).reduce((sum, day) => sum + day.prayerDuration, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;

